import React, { useEffect, useState, useCallback, useMemo } from "react";
import { RefreshCw, Search, Zap, Sparkles, X, AlertCircle, Check } from "lucide-react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { traders } from "../../copyTradingMock";

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const AdminCopyTrading = () => {
  const [copies, setCopies] = useState([]);
  const [statusFilter, setStatusFilter] = useState("active");
  const [traderFilter, setTraderFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [distOpen, setDistOpen] = useState(null); // trader_id
  const [distPct, setDistPct] = useState(1);
  const [distNote, setDistNote] = useState("");
  const [distBusy, setDistBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: statusFilter };
      if (traderFilter !== "all") params.trader_id = traderFilter;
      const { data } = await api.get("/admin/copies", { params });
      setCopies(data);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, traderFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = copies.filter((c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.user_email || "").toLowerCase().includes(s) || (c.user_name || "").toLowerCase().includes(s);
  });

  // group by trader for the "distribute" panel
  const grouped = useMemo(() => {
    const m = {};
    copies.forEach((c) => {
      if (c.status !== "active") return;
      if (!m[c.trader_id]) m[c.trader_id] = { name: c.trader_name, color: c.traderColor || c.trader_color, count: 0, allocated: 0, value: 0 };
      m[c.trader_id].count += 1;
      m[c.trader_id].allocated += Number(c.allocated || 0);
      m[c.trader_id].value += Number(c.current_value || 0);
    });
    return m;
  }, [copies]);

  const handleDistribute = async () => {
    if (!distOpen) return;
    setDistBusy(true); setErr(null); setMsg(null);
    try {
      const { data } = await api.post("/admin/copies/distribute", {
        trader_id: distOpen,
        pct: Number(distPct),
        note: distNote.trim() || null,
      });
      setMsg(`Applied ${distPct}% to ${data.affected} copies (Δ $${fmt(data.total_pnl)})`);
      setDistOpen(null); setDistPct(1); setDistNote("");
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed");
    } finally {
      setDistBusy(false);
      setTimeout(() => setMsg(null), 4000);
    }
  };

  const forceStop = async (id) => {
    if (!window.confirm("Force-stop this allocation? P&L will settle to user balance.")) return;
    try {
      await api.delete(`/admin/copies/${id}`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <AdminLayout title="Copy Trading" subtitle="Manage trader copies & P&L distribution">
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={14} className="text-[#ffae6a]" />
          <div className="text-[12px] uppercase tracking-wider font-semibold text-white/55">Distribute P&amp;L by trader</div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {traders.map((t) => {
            const g = grouped[t.id];
            return (
              <div key={t.id} className="bb-card rounded-2xl p-4 relative overflow-hidden">
                <div className="absolute -top-8 -right-8 w-28 h-28 rounded-full blur-3xl opacity-40" style={{ background: t.color }}></div>
                <div className="relative">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center text-[11px] font-bold text-white" style={{ background: `linear-gradient(135deg, ${t.color}, ${t.accent})` }}>
                      {t.avatar}
                    </div>
                    <div className="min-w-0">
                      <div className="text-[13px] text-white font-medium truncate">{t.name}</div>
                      <div className="text-[10px] text-white/45">{t.monthlyReturn}%/mo</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div>
                      <div className="text-[10px] text-white/45">Copiers</div>
                      <div className="text-[14px] text-white font-semibold">{g?.count || 0}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-white/45">In copies</div>
                      <div className="text-[14px] text-white font-semibold tabular-nums">${fmt(g?.value || 0)}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => { setDistOpen(t.id); setDistPct(1); setDistNote(""); }}
                    disabled={!g?.count}
                    className="w-full h-9 rounded-lg text-white text-[12px] font-medium inline-flex items-center justify-center gap-1.5 disabled:opacity-40"
                    style={{ background: "linear-gradient(135deg,#ff7a1a,#c2410c)" }}
                  >
                    <Zap size={12} /> Apply return
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        {msg && <div className="mt-3 inline-flex items-center gap-1.5 text-[12px] text-emerald-400"><Check size={13} /> {msg}</div>}
      </div>

      <div className="bb-card rounded-2xl overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-white/5">
          <div className="flex items-center gap-1 bg-white/[0.04] rounded-lg p-1 border border-white/8">
            {["active", "paused", "stopped", "all"].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 text-[12px] rounded-md whitespace-nowrap capitalize transition-colors ${
                  statusFilter === s ? "bg-white text-[#07080d] font-medium" : "text-white/55 hover:text-white"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <select
              value={traderFilter}
              onChange={(e) => setTraderFilter(e.target.value)}
              className="h-9 px-3 bg-white/[0.04] border border-white/8 rounded-lg text-[12px] text-white outline-none focus:border-[#ff7a1a]"
            >
              <option value="all">All traders</option>
              {traders.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <div className="flex items-center gap-2 px-3 h-9 bg-white/[0.04] border border-white/8 rounded-lg">
              <Search size={14} className="text-white/40" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search user"
                className="bg-transparent outline-none text-[12px] text-white placeholder:text-white/35 w-44"
              />
            </div>
            <button onClick={load} className="w-9 h-9 rounded-lg border border-white/8 text-white/70 hover:text-white flex items-center justify-center">
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] min-w-[1000px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-white/40 font-semibold bg-white/[0.02]">
                <th className="text-left py-3 px-5">User</th>
                <th className="text-left py-3">Trader</th>
                <th className="text-right py-3">Allocated</th>
                <th className="text-right py-3">Current value</th>
                <th className="text-right py-3">P&amp;L</th>
                <th className="text-left py-3">Status</th>
                <th className="text-right py-3 pr-5"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const pnl = Number(c.current_value || 0) - Number(c.allocated || 0);
                return (
                  <tr key={c.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="py-3.5 px-5">
                      <div className="text-white font-medium">{c.user_name}</div>
                      <div className="text-white/45 text-[11px]">{c.user_email}</div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold" style={{ background: c.trader_color || "#1c3df5" }}>
                          {c.trader_avatar}
                        </div>
                        <span className="text-white text-[12px]">{c.trader_name}</span>
                        <span className="text-white/40 text-[10px]">{c.multiplier}x</span>
                      </div>
                    </td>
                    <td className="text-right text-white/80 tabular-nums">${fmt(c.allocated)}</td>
                    <td className="text-right text-white font-semibold tabular-nums">${fmt(c.current_value)}</td>
                    <td className={`text-right tabular-nums font-semibold ${pnl >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                      {pnl >= 0 ? "+" : ""}${fmt(pnl)}
                    </td>
                    <td>
                      <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${
                        c.status === "active" ? "text-emerald-400 bg-emerald-400/10" :
                        c.status === "paused" ? "text-amber-400 bg-amber-400/10" :
                        "text-white/45 bg-white/5"
                      }`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="text-right pr-5">
                      {c.status !== "stopped" && (
                        <button onClick={() => forceStop(c.id)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-white/10 text-white/55 hover:bg-rose-500/10 hover:border-rose-400 hover:text-rose-400 text-[11px]">
                          <X size={11} /> Force stop
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!loading && filtered.length === 0 && (
          <div className="py-16 text-center text-white/40 text-[13px]">No copies match these filters.</div>
        )}
      </div>

      {distOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setDistOpen(null)}></div>
          <div className="relative bb-card rounded-3xl p-6 w-full max-w-md">
            <div className="font-display font-bold text-[18px] mb-1">Apply return</div>
            <p className="text-[12px] text-white/55 mb-4">
              Apply this % return to every active copy of <span className="text-white font-medium">{traders.find((t) => t.id === distOpen)?.name}</span>.
            </p>
            <div className="mb-3">
              <div className="text-[12px] text-white/55 mb-1.5">Return %</div>
              <input
                type="number"
                step="0.1"
                value={distPct}
                onChange={(e) => setDistPct(Number(e.target.value))}
                className="w-full h-11 px-3 bg-white/[0.02] border border-white/10 rounded-xl text-[16px] font-display font-bold text-white outline-none focus:border-[#ff7a1a] tabular-nums"
              />
              <div className="flex gap-2 mt-2">
                {[0.5, 1, 2, 5, 10].map((v) => (
                  <button key={v} onClick={() => setDistPct(v)} className="px-2 py-1 rounded-md border border-white/8 hover:border-white/25 text-white/70 text-[11px]">
                    +{v}%
                  </button>
                ))}
                {[-1, -2, -5].map((v) => (
                  <button key={v} onClick={() => setDistPct(v)} className="px-2 py-1 rounded-md border border-white/8 hover:border-rose-400 text-rose-300 text-[11px]">
                    {v}%
                  </button>
                ))}
              </div>
            </div>
            <input
              value={distNote}
              onChange={(e) => setDistNote(e.target.value)}
              placeholder="Note (optional, logged)"
              className="w-full h-10 px-3 mb-4 bg-white/[0.02] border border-white/10 rounded-lg text-[12px] text-white outline-none focus:border-[#ff7a1a] placeholder:text-white/30"
            />
            {err && <div className="mb-3 flex items-center gap-1.5 text-[11px] text-rose-300"><AlertCircle size={12} /> {err}</div>}
            <div className="flex justify-end gap-2">
              <button onClick={() => setDistOpen(null)} className="px-4 py-2 rounded-lg border border-white/10 text-white/70 text-[13px]">Cancel</button>
              <button onClick={handleDistribute} disabled={distBusy} className="px-4 py-2 rounded-lg text-white text-[13px] font-medium disabled:opacity-60" style={{ background: "linear-gradient(135deg,#ff7a1a,#c2410c)" }}>
                {distBusy ? "Applying…" : "Apply return"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminCopyTrading;
